import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

function CustomerEditPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone_number: "",
    is_active: false,
  });

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL || "https://installmentzone.com/api";

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        setLoading(true);
        setError(null);
        setSubmitError(null);

        const response = await fetch(
          `${API_BASE_URL}/customers/show.php?id=${customerId}`,
          { cache: "no-store" }
        );
        const result = await response.json();

        if (result.success && result.data) {
          setCustomer(result.data);
          setFormData({
            first_name: result.data.first_name || "",
            last_name: result.data.last_name || "",
            phone_number: result.data.phone_number || "",
            is_active: result.data.is_active === 1 || result.data.is_active === true,
          });
        } else {
          setError(result.message || "Failed to fetch customer details.");
        }
      } catch (err) {
        console.error("Error fetching customer details:", err);
        setError("An unexpected error occurred while fetching customer data.");
      } finally {
        setLoading(false);
      }
    };

    if (customerId) {
      fetchCustomer();
    } else {
      setError("Customer ID is missing.");
      setLoading(false);
    }
  }, [customerId, API_BASE_URL]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);

    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      setSubmitError("First name and last name are required.");
      setIsSubmitting(false);
      return;
    }

    try {
      const payload = { ...formData };

      const response = await fetch(
        `${API_BASE_URL}/customers/update.php?id=${customerId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (result.success) {
        navigate("/customers", {
          state: {
            notificationMessage: `Customer "${formData.first_name} ${formData.last_name}" updated successfully!`,
            timestamp: Date.now(),
          },
        });
      } else {
        setSubmitError(result.message || "Failed to update customer details.");
      }
    } catch (err) {
      console.error("Error updating customer details:", err);
      setSubmitError("An unexpected network error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading)
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "linear-gradient(135deg, #1E3A8A, #3B82F6, #60A5FA)",
          color: "#fff",
          fontFamily: "Segoe UI, sans-serif",
          fontSize: "18px",
        }}
      >
        Loading customer details...
      </div>
    );

  if (error)
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #1E3A8A, #3B82F6, #60A5FA)",
          color: "#fff",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          fontFamily: "Segoe UI, sans-serif",
          padding: "20px",
        }}
      >
        <h2 style={{ color: "#F87171", marginBottom: "10px" }}>
          Error Loading Customer
        </h2>
        <p style={{ textAlign: "center", maxWidth: "400px" }}>{error}</p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: "15px",
            background: "#1E3A8A",
            color: "#fff",
            border: "none",
            padding: "10px 20px",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "500",
          }}
        >
          Retry
        </button>
      </div>
    );

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #1E3A8A, #3B82F6, #60A5FA)",
        padding: "40px 20px",
        fontFamily: "Segoe UI, sans-serif",
        color: "#fff",
      }}
    >
      <div
        style={{
          background: "rgba(255, 255, 255, 0.15)",
          borderRadius: "15px",
          maxWidth: "700px",
          margin: "0 auto",
          padding: "30px",
          boxShadow: "0 6px 15px rgba(0, 0, 0, 0.2)",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            fontSize: "28px",
            fontWeight: "600",
            marginBottom: "25px",
          }}
        >
          Edit Customer {customer ? `(ID: ${customer.id})` : ""}
        </h2>

        {submitError && (
          <p
            style={{
              background: "rgba(248, 113, 113, 0.1)",
              color: "#F87171",
              border: "1px solid #F87171",
              borderRadius: "6px",
              padding: "10px",
              marginBottom: "15px",
              textAlign: "center",
            }}
          >
            {submitError}
          </p>
        )}

        <form
          onSubmit={handleFormSubmit}
          style={{
            background: "rgba(255, 255, 255, 0.9)",
            color: "#1E293B",
            borderRadius: "10px",
            padding: "25px",
            boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
          }}
        >
          <div style={{ marginBottom: "15px" }}>
            <label
              htmlFor="first_name"
              style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}
            >
              First Name:
            </label>
            <input
              id="first_name"
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              required
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "6px",
                border: "1px solid #CBD5E1",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div style={{ marginBottom: "15px" }}>
            <label
              htmlFor="last_name"
              style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}
            >
              Last Name:
            </label>
            <input
              id="last_name"
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              required
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "6px",
                border: "1px solid #CBD5E1",
              }}
            />
          </div>

          <div style={{ marginBottom: "15px" }}>
            <label
              htmlFor="phone_number"
              style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}
            >
              Phone Number:
            </label>
            <input
              id="phone_number"
              type="tel"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleInputChange}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "6px",
                border: "1px solid #CBD5E1",
              }}
            />
          </div>

          <div style={{ marginBottom: "20px", display: "flex", alignItems: "center" }}>
            <input
              type="checkbox"
              id="is_active"
              name="is_active"
              checked={formData.is_active}
              onChange={handleInputChange}
              style={{ marginRight: "10px", transform: "scale(1.2)" }}
            />
            <label htmlFor="is_active" style={{ fontWeight: "500" }}>
              Active
            </label>
          </div>

          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={() => navigate("/customers")}
              disabled={isSubmitting}
              style={{
                background: "#6B7280",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                padding: "10px 16px",
                cursor: "pointer",
                fontWeight: "500",
              }}
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                background: "#1E3A8A",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                padding: "10px 16px",
                cursor: "pointer",
                fontWeight: "500",
              }}
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CustomerEditPage;
